import React, { useState, useMemo } from 'react';
import { useFirstInvoicesList } from './service';
import moment from 'moment';
import 'moment/locale/tr';
import usePermission from 'src/app/permissions/use-permission';
import { KTSVG } from 'src/_metronic/helpers';

/**
 * İlk Fatura Kesilenler Dashboard Widget
 * Farklı zaman dilimlerinde ilk faturaları listeler
 */

type TabType = 'day' | 'week' | 'lastWeek' | 'month' | 'quarter' | 'year';

// Tab seçenekleri
const tabOptions: { value: TabType; label: string; shortLabel: string }[] = [
  { value: 'day', label: 'Bugün', shortLabel: 'Bugün' },
  { value: 'week', label: 'Bu Hafta', shortLabel: 'Bu Hafta' },
  { value: 'lastWeek', label: 'Geçen Hafta', shortLabel: 'Geçen Hafta' },
  { value: 'month', label: 'Bu Ay', shortLabel: 'Bu Ay' },
  { value: 'quarter', label: 'Bu Çeyrek', shortLabel: 'Çeyrek' },
  { value: 'year', label: 'Bu Yıl', shortLabel: 'Yıl' },
];

// Tutar formatlama
const formatCurrency = (amount: number) => {
  if (!amount) return '-';
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const FirstInvoiceWidget: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('week');
  const [showDropdown, setShowDropdown] = useState(false);

  // İzin kontrolü
  const [permLoading, hasViewPermission] = usePermission('ROLE_CRM_CUSTOMER_FIRST_INVOICE');

  // Tarih aralıklarını hesapla
  const dateFilters = useMemo(() => {
    const today = moment();

    return {
      day: {
        start_date: today.format('YYYY-MM-DD'),
        end_date: today.format('YYYY-MM-DD'),
      },
      week: {
        start_date: today.clone().startOf('isoWeek').format('YYYY-MM-DD'),
        end_date: today.clone().endOf('isoWeek').format('YYYY-MM-DD'),
      },
      lastWeek: {
        start_date: today.clone().subtract(1, 'week').startOf('isoWeek').format('YYYY-MM-DD'),
        end_date: today.clone().subtract(1, 'week').endOf('isoWeek').format('YYYY-MM-DD'),
      },
      month: {
        start_date: today.clone().startOf('month').format('YYYY-MM-DD'),
        end_date: today.clone().endOf('month').format('YYYY-MM-DD'),
      },
      quarter: {
        start_date: today.clone().startOf('quarter').format('YYYY-MM-DD'),
        end_date: today.clone().endOf('quarter').format('YYYY-MM-DD'),
      },
      year: {
        start_date: today.clone().startOf('year').format('YYYY-MM-DD'),
        end_date: today.clone().endOf('year').format('YYYY-MM-DD'),
      },
    };
  }, []);

  // Aktif filtreye göre veriyi çek
  const { data: currentData, isLoading } = useFirstInvoicesList({
    page: 1,
    limit: 5,
    sortBy: 'desc',
    ...dateFilters[activeTab],
  });

  // İzin yoksa widget'i gösterme
  if (!permLoading && !hasViewPermission) {
    return null;
  }

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="col-xl-6">
        <div className="card card-xl-stretch mb-xl-8">
          <div className="card-header border-0  pt-5">
            <h3 className="card-title align-items-start flex-column">
              <span className="card-label fw-bolder fs-3 mb-1">İlk Fatura Kesilenler</span>
              <span className="text-muted mt-1 fw-bold fs-7">Yükleniyor...</span>
            </h3>
          </div>
          <div className="card-body py-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="d-flex align-items-center mb-5">
                <div className="placeholder-glow me-3">
                  <div className="placeholder bg-secondary rounded-circle" style={{ width: '40px', height: '40px' }}></div>
                </div>
                <div className="d-flex flex-column flex-grow-1">
                  <div className="placeholder-glow">
                    <span className="placeholder col-7 bg-secondary mb-2"></span>
                    <span className="placeholder col-4 bg-secondary"></span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const invoices = currentData?.results || [];
  const totalResults = currentData?.totalResults || 0;

  const getTabLabel = () => {
    const option = tabOptions.find((o) => o.value === activeTab);
    return `${option?.label || ''} ${totalResults} ilk fatura`;
  };

  const activeOption = tabOptions.find((o) => o.value === activeTab);

  const renderInvoiceRow = (invoice: any) => (
    <tr key={invoice.id}>
      <td>
        <span className="text-dark fw-bolder text-hover-primary d-block mb-1 fs-6">{invoice.customer_name || 'İsimsiz Müşteri'}</span>
        <span className="text-muted fw-bold d-block fs-7">
          {invoice.sales_staff_name} {invoice.sales_staff_surname?.charAt(0)}.
        </span>
      </td>
      <td className="text-end">
        <span className="fw-bolder text-dark d-block fs-7">{moment(invoice.invoice_date).format('DD MMM YYYY')}</span>
        <span className="text-muted fw-bold d-block fs-8">{invoice.invoice_number || '-'}</span>
      </td>
    </tr>
  );

  const renderEmptyState = () => (
    <tr>
      <td colSpan={3} className="text-center py-10">
        <div className="d-flex flex-column align-items-center">
          <KTSVG path="/media/icons/duotone/Shopping/Dollar.svg" className="svg-icon-2x svg-icon-muted mb-3" />
          <span className="text-muted fs-6">{activeOption?.label} için ilk fatura kaydı bulunamadı</span>
        </div>
      </td>
    </tr>
  );

  return (
    <div className="col-xl-6">
      <div className="card card-xl-stretch mb-xl-8">
        {/* Header */}
        <div className="card-header border-0 pt-5 bg-success bg-opacity-25 pb-2">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bolder fs-3 mb-1">İlk Fatura Kesilenler</span>
            <span className="text-muted mt-1 fw-bold fs-7">{getTabLabel()}</span>
          </h3>
          <div className="card-toolbar d-flex align-items-center gap-2">
            {/* Dropdown Filtre */}
            <div className="position-relative">
              <button type="button" className="btn btn-sm btn-light-success d-flex align-items-center gap-2 fw-bold" onClick={() => setShowDropdown(!showDropdown)}>
                <KTSVG path="/media/icons/duotone/Interface/Calendar.svg" className="svg-icon-6" />
                <span>{activeOption?.shortLabel}</span>
                <KTSVG path={`/media/icons/duotone/Navigation/Angle-${showDropdown ? 'up' : 'down'}.svg`} className="svg-icon-8" />
              </button>
              {showDropdown && (
                <>
                  <div className="position-fixed top-0 start-0 w-100 h-100" style={{ zIndex: 99 }} onClick={() => setShowDropdown(false)}></div>
                  <div className="position-absolute end-0 mt-1 py-2 bg-white rounded shadow-sm border" style={{ zIndex: 100, minWidth: '140px' }}>
                    {tabOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`dropdown-item px-4 py-2 d-flex align-items-center justify-content-between ${
                          activeTab === option.value ? 'active bg-light-success text-success' : ''
                        }`}
                        onClick={() => {
                          setActiveTab(option.value);
                          setShowDropdown(false);
                        }}
                      >
                        <span>{option.label}</span>
                        {activeTab === option.value && <KTSVG path="/media/icons/duotone/Navigation/Check.svg" className="svg-icon-6 svg-icon-success" />}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        {/*end::Header*/}
        {/*begin::Body*/}
        <div className="card-body py-3">
          {/*begin::Table container*/}
          <div className="table-responsive">
            {/*begin::Table*/}
            <table className="table table-row-dashed table-row-gray-200 align-middle gs-0 gy-4">
              {/*begin::Table head*/}
              <thead>
                <tr className="border-0">
                  <th className="p-0 min-w-150px" />
                  <th className="p-0 min-w-100px" />
                </tr>
              </thead>
              {/*end::Table head*/}
              {/*begin::Table body*/}
              <tbody>{invoices.length > 0 ? invoices.map(renderInvoiceRow) : renderEmptyState()}</tbody>
              {/*end::Table body*/}
            </table>
          </div>
          {/*end::Table*/}
          {totalResults > 5 && (
            <div className="d-flex justify-content-center mt-4">
              <a href="/first-invoices" className="btn btn-sm btn-light-success">
                Tümünü Görüntüle ({totalResults})
              </a>
            </div>
          )}
        </div>
        {/*end::Body*/}
      </div>
    </div>
  );
};

export default FirstInvoiceWidget;
